import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 転床情報 API 操作クラス
 */
export default class MoveRoomSchedules {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET MoveRoomSchedule
   */
  public async get(userStore: LoginUser, startDatetime: number, roomId: number, residentId: number): Promise<MoveRoomSchedulesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      startDatetime: startDatetime,
      roomId: roomId,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.MoveRoomSchedule, formatParams);

    const response = await this.commonService.executeGet<MoveRoomSchedulesGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET MoveRoomSchedules
   */
  public async gets(userStore: LoginUser, safetyConfirmsId: number, params?: MoveRoomSchedulesGetsParam): Promise<Array<MoveRoomSchedulesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: safetyConfirmsId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.MoveRoomSchedules, formatParams);

    const response = await this.commonService.executeGet<Array<MoveRoomSchedulesGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET MoveRoomSchedules Count
   */
  public async getCount(userStore: LoginUser, safetyConfirmsId: number, params?: MoveRoomSchedulesGetCountParam): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: safetyConfirmsId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.MoveRoomSchedules, formatParams);

    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### POST MoveRoomSchedules
   */
  public async post(userStore: LoginUser, startDatetime: number, roomId: number, residentId: number, requestBody: MoveRoomSchedulesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      startDatetime: startDatetime,
      roomId: roomId,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.MoveRoomSchedule, formatParams);

    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH MoveRoomSchedules
   */
  public async patch(userStore: LoginUser, startDatetime: number, roomId: number, residentId: number, requestBody: MoveRoomSchedulesPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      startDatetime: startDatetime,
      roomId: roomId,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.MoveRoomSchedule, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### DELETE MoveRoomSchedules
   */
  public async delete(userStore: LoginUser, startDatetime: number, roomId: number, residentId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      startDatetime: startDatetime,
      roomId: roomId,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.MoveRoomSchedule, formatParams);

    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface MoveRoomSchedulesGetModel {
  /** 施設ID */
  facilityId: number;
  /** ソートキー */
  moveRoomSchedulesSortKey: string;
  /** 部屋使用開始日時 */
  startDatetime: number;
  /** 部屋使用終了日時 */
  endDatetime: number;
  /** 部屋ID */
  roomId: number;
  /** 部屋名 */
  roomName: string;
  /** 利用者ID */
  residentId: number;
  /** 利用者名 */
  residentName: string;
  /** タイプ */
  type: MoveRoomSchedulesType;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

/**
 * ### タイプ
 */
export enum MoveRoomSchedulesType {
  CHANGE_ROOM = 0,
  TEMPORARY = 1
}

export interface MoveRoomSchedulesGetsParam {
  start?: string;
  end?: string;
  startIndex?: string;
  endIndex?: string;
}

export interface MoveRoomSchedulesGetCountParam {
  start?: string;
  end?: string;
}

export interface MoveRoomSchedulesPostUrlParam {
  /** 部屋使用開始日時 */
  startDatetime: number;
  /** 部屋ID */
  roomId: number;
  /** 利用者ID */
  residentId: number;
}

export interface MoveRoomSchedulesPostModel {
  /** 部屋使用終了日時 */
  endDatetime: number;
  /** 部屋名 */
  roomName: string;
  /** 利用者名 */
  residentName: string;
  /** タイプ */
  type: MoveRoomSchedulesType;
}

export interface MoveRoomSchedulesPatchUrlParam {
  /** 部屋使用開始日時 */
  startDatetime: number;
  /** 部屋ID */
  roomId: number;
  /** 利用者ID */
  residentId: number;
}

export interface MoveRoomSchedulesPatchModel {
  /** 更新後部屋使用開始日時 */
  newStartDatetime: number;
  /** 更新後部屋使用終了日時 */
  newEndDatetime: number;
  /** 更新後部屋ID */
  newRoomId: number;
  /** 更新後部屋名 */
  newRoomName: string;
  /** 更新後利用者名 */
  newResidentName: string;
  /** タイプ */
  type: MoveRoomSchedulesType;
  /** 最終更新日 */
  updated: number;
}
